import type { SelectProps } from "antd";
import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import * as React from "react";
import { ZAttrMeta } from "src/business/attribute/ZAttrMeta";
import { onError } from "src/common/onError";

import { loadSpecification } from "../apiSpecifications";
import { SpecificationType } from "../ZSpecification";

type PropsSpecificationAttrSelect = Omit<SelectProps, "loading" | "options"> & {
  /** ID спецификации для загрузки атрибутов */
  specId?: string;
  /** Тип спецификации */
  specType?: SpecificationType;
  /** Функция фильтрации атрибутов. Используйте React.useCallback для стабильной ссылки */
  filterFn?: (attr: ZAttrMeta) => boolean;
};

/**
 * Select для выбора атрибута из спецификации
 * Автоматически загружает и отображает атрибуты указанной спецификации
 *
 * @example
 * <SpecificationAttrSelect
 *   specId={form.getFieldValue("schema")}
 *   filterFn={isScalarAttrType}
 *   allowClear
 * />
 */

export const SpecificationAttrSelect: React.FC<PropsSpecificationAttrSelect> = (
  props,
) => {
  const { specId, specType = "primary", filterFn, ...restSelectProps } = props;
  const [options, setOptions] = React.useState<DefaultOptionType[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!specId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    loadSpecification(specType, specId)
      .then((spec) => {
        let attrs = spec.definition?.attributes ?? [];
        if (filterFn) {
          attrs = attrs.filter(filterFn);
        }
        const opts = attrs.map((attr) => ({
          value: attr.uid,
          label: attr.title || attr.name,
        }));
        setOptions(opts);
      })
      .catch(onError)
      .finally(() => {
        setLoading(false);
      });
  }, [specId, specType]);

  return <Select {...restSelectProps} options={options} loading={loading} />;
};
